import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarDays, Check, ChevronRight, Clock, Grid3x3, Heart, Loader2, MessageCircle, Plus, RefreshCw, Sparkles, Trash2, X } from 'lucide-react';
import { FacebookIcon, InstagramIcon, LinkedInIcon } from './PlatformIcon';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import { SOCIAL_ENABLED } from '../lib/featureFlags';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import { postPlacementLabel } from '../lib/postPlacement';
import CreateSocialPostModal from './CreateSocialPostModal';
import SocialConnectButton from './SocialConnectButton';
import { SocialCommentsInbox, SocialPostSheet } from './SocialEngagement';
import type { SocialPlatform } from './SocialConnectButton';

type SocialPost = {
  id: string;
  post: string;
  postUrl: string | null;
  created: string | null;
  fullPicture: string | null;
  likeCount: number | null;
  commentsCount: number | null;
  canDelete: boolean;
  ayrsharePostId: string | null;
  nexusHistoryId: string | null;
};

type ConnectionInfo = {
  id: string;
  displayName: string | null;
  username: string | null;
  avatarUrl?: string | null;
  followersCount?: number | null;
  followsCount?: number | null;
  mediaCount?: number | null;
  fanCount?: number | null;
};

type MonthlyMetric = { value: number; deltaPct: number | null };

type MonthlyStats = {
  timeZone: string;
  monthLabel: string;
  views: MonthlyMetric | null;
  reach: MonthlyMetric | null;
  newFollowers: MonthlyMetric | null;
  engagement: MonthlyMetric | null;
};

type UpcomingDraft = {
  id: string;
  content: string;
  scheduled_for: string | null;
  source: string | null;
  has_image: boolean;
  media_id: string | null;
  media_library?: { url: string } | { url: string }[] | null;
  metadata?: { postFormat?: string } | null;
};

const PLATFORM_UI: Record<
  SocialPlatform,
  {
    label: string;
    accountLabel: string;
    functionName: string;
    Icon: typeof FacebookIcon;
    accentClass: string;
    primaryBtnClass: string;
    connectTitle: string;
    connectHint: string;
    publishedEmpty: string;
    publishedHint: string;
  }
> = {
  facebook: {
    label: 'Facebook',
    accountLabel: 'Facebook Page',
    functionName: 'ayrshare-facebook-posts',
    Icon: FacebookIcon,
    accentClass: 'bg-[#1877f2]',
    primaryBtnClass: 'bg-[#1877f2] text-white hover:bg-[#166fe5]',
    connectTitle: 'Connect Facebook',
    connectHint: 'Connect a Facebook Page to stage and publish posts from Nexus',
    publishedEmpty: 'No published Facebook posts yet',
    publishedHint: 'Tap a post for comments, replies, and reach · delete available for posts published from Nexus',
  },
  instagram: {
    label: 'Instagram',
    accountLabel: 'Instagram',
    functionName: 'ayrshare-instagram-posts',
    Icon: InstagramIcon,
    accentClass: 'bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af]',
    primaryBtnClass: 'bg-[#E1306C] text-white hover:bg-[#c13584]',
    connectTitle: 'Connect Instagram',
    connectHint:
      'Connect an Instagram Business or Creator account to stage and publish posts from Nexus',
    publishedEmpty: 'No published Instagram posts yet',
    publishedHint: 'Tap a post for comments, replies, and reach · delete available for posts published from Nexus',
  },
  linkedin: {
    label: 'LinkedIn',
    accountLabel: 'LinkedIn',
    functionName: 'ayrshare-linkedin-posts',
    Icon: LinkedInIcon,
    accentClass: 'bg-[#0A66C2]',
    primaryBtnClass: 'bg-[#0A66C2] text-white hover:bg-[#004182]',
    connectTitle: 'Connect LinkedIn',
    connectHint: 'Connect a personal profile or company page to publish posts from Nexus',
    publishedEmpty: 'No published LinkedIn posts yet',
    publishedHint: 'Tap a post for comments, replies, and impressions · delete available for posts published from Nexus',
  },
};

function formatWhen(iso: string | null, timeZone?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: timeZone ? 'short' : undefined,
  });
}

/** 1272 → "1,272", 15400 → "15.4K", 2300000 → "2.3M" */
function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

/** "Tue Sep 15 · 12pm" — compact stamp for the upcoming strip. */
function formatStripWhen(iso: string | null, timeZone?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d
    .toLocaleDateString(undefined, { timeZone, weekday: 'short', month: 'short', day: 'numeric' })
    .replace(',', '');
  const time = d
    .toLocaleTimeString(undefined, { timeZone, hour: 'numeric', minute: '2-digit' })
    .replace(':00', '')
    .replace(/\s/g, '')
    .toLowerCase();
  return `${date} · ${time}`;
}

function mediaUrlOf(d: UpcomingDraft): string | null {
  const rel = d.media_library;
  if (Array.isArray(rel)) return rel[0]?.url || null;
  return rel?.url || null;
}

export default function FacebookWorkspace({
  platform = 'facebook',
  onConnectionChange,
}: {
  platform?: SocialPlatform;
  onConnectionChange?: (connected: boolean) => void;
}) {
  const ui = PLATFORM_UI[platform];
  const PlatformIcon = ui.Icon;
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const displayTz =
    selectedBusiness?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [checking, setChecking] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connection, setConnection] = useState<ConnectionInfo | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingDraft[]>([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [autoPostsEnabled, setAutoPostsEnabled] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [openDraft, setOpenDraft] = useState<UpcomingDraft | null>(null);
  const [draftActionId, setDraftActionId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [fillingQueue, setFillingQueue] = useState(false);
  const [openPost, setOpenPost] = useState<SocialPost | null>(null);
  const [inboxKey, setInboxKey] = useState(0);

  const onConnectionChangeRef = useRef(onConnectionChange);
  const connectedRef = useRef(connected);
  const connectionIdRef = useRef<string | null>(null);
  const loadedForRef = useRef<string | null>(null);

  useEffect(() => {
    onConnectionChangeRef.current = onConnectionChange;
  }, [onConnectionChange]);

  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);

  useEffect(() => {
    connectionIdRef.current = connection?.id ?? null;
  }, [connection?.id]);

  const refreshConnection = useCallback(async () => {
    if (!SOCIAL_ENABLED || !businessId) {
      setConnected(false);
      setConnection(null);
      setChecking(false);
      if (connectedRef.current) onConnectionChangeRef.current?.(false);
      return;
    }
    try {
      const { data } = await supabase
        .from('social_media_connections')
        .select('id, display_name, username')
        .eq('business_id', businessId)
        .eq('platform', platform)
        .eq('provider', 'ayrshare')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      const nextConnected = !!data;
      setConnected(nextConnected);
      setConnection(
        data
          ? {
              id: data.id as string,
              displayName: (data.display_name as string) || null,
              username: (data.username as string) || null,
            }
          : null,
      );
      if (connectedRef.current !== nextConnected) {
        onConnectionChangeRef.current?.(nextConnected);
      }
    } catch (err) {
      console.error(err);
      setConnected(false);
      if (connectedRef.current) onConnectionChangeRef.current?.(false);
    } finally {
      setChecking(false);
    }
  }, [businessId, platform]);

  const loadSettings = useCallback(async (connectionId: string) => {
    const { data } = await supabase
      .from('social_auto_post_settings')
      .select('enabled')
      .eq('connection_id', connectionId)
      .maybeSingle();
    setAutoPostsEnabled(!!data?.enabled);
  }, []);

  const loadUpcoming = useCallback(async () => {
    if (!businessId || !connectionIdRef.current) {
      setUpcoming([]);
      return;
    }
    setLoadingUpcoming(true);
    try {
      const attempts = [
        'id, content, scheduled_for, source, has_image, media_id, metadata, media_library!social_post_drafts_media_id_fkey(url)',
        'id, content, scheduled_for, source, has_image, media_id, metadata, media_library!media_id(url)',
        'id, content, scheduled_for, source, has_image, media_id, metadata',
      ];
      let rows: UpcomingDraft[] = [];
      let lastError: unknown = null;
      for (const select of attempts) {
        const { data, error } = await supabase
          .from('social_post_drafts')
          .select(select)
          .eq('business_id', businessId)
          .eq('connection_id', connectionIdRef.current)
          .eq('platform', platform)
          .eq('status', 'pending')
          .order('scheduled_for', { ascending: true, nullsFirst: false })
          .limit(20);
        if (!error) {
          rows = (data as UpcomingDraft[]) || [];
          lastError = null;
          break;
        }
        lastError = error;
      }
      if (lastError) throw lastError;

      const missingIds = rows
        .filter((r) => r.media_id && !mediaUrlOf(r))
        .map((r) => r.media_id as string);
      if (missingIds.length > 0) {
        const { data: mediaRows } = await supabase
          .from('media_library')
          .select('id, url')
          .in('id', [...new Set(missingIds)]);
        const urlById = new Map((mediaRows || []).map((m) => [m.id as string, m.url as string]));
        rows = rows.map((r) => {
          if (!r.media_id || mediaUrlOf(r)) return r;
          const url = urlById.get(r.media_id);
          return url ? { ...r, media_library: { url } } : r;
        });
      }

      rows.sort((a, b) => {
        const ta = a.scheduled_for ? new Date(a.scheduled_for).getTime() : Number.MAX_SAFE_INTEGER;
        const tb = b.scheduled_for ? new Date(b.scheduled_for).getTime() : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });
      setUpcoming(rows);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load upcoming posts');
      setUpcoming([]);
    } finally {
      setLoadingUpcoming(false);
    }
  }, [businessId, platform]);

  const loadPosts = useCallback(async (refresh = false) => {
    if (!businessId || !connectedRef.current) {
      setPosts([]);
      return;
    }
    setLoadingPosts(true);
    try {
      const { data, error } = await supabase.functions.invoke(ui.functionName, {
        body: {
          action: 'list',
          businessId,
          connectionId: connectionIdRef.current,
          limit: 24,
          refresh,
        },
      });
      if (error || data?.error) {
        toast.error(
          data?.error ||
            (await invokeErrorMessage(error, data)) ||
            `Failed to load ${ui.label} posts`,
        );
        setPosts([]);
        return;
      }
      if (data?.connection?.id) {
        const c = data.connection;
        setConnection({
          id: c.id,
          displayName: c.displayName || null,
          username: c.username || null,
          avatarUrl: c.avatarUrl || null,
          followersCount: typeof c.followersCount === 'number' ? c.followersCount : null,
          followsCount: typeof c.followsCount === 'number' ? c.followsCount : null,
          mediaCount: typeof c.mediaCount === 'number' ? c.mediaCount : null,
          fanCount: typeof c.fanCount === 'number' ? c.fanCount : null,
        });
      }
      setMonthlyStats((data?.monthlyStats as MonthlyStats) || null);
      setPosts((data?.posts as SocialPost[]) || []);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to load ${ui.label} posts`);
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  }, [businessId, ui.functionName, ui.label]);

  const refreshAll = useCallback(async (refreshPosts = false) => {
    if (!connectionIdRef.current) return;
    await Promise.all([
      loadSettings(connectionIdRef.current),
      loadUpcoming(),
      loadPosts(refreshPosts),
    ]);
    loadedForRef.current = connectionIdRef.current;
  }, [loadSettings, loadUpcoming, loadPosts]);

  useEffect(() => {
    loadedForRef.current = null;
    setChecking(true);
    setComposerOpen(false);
    setPosts([]);
    setUpcoming([]);
    setMonthlyStats(null);
    void refreshConnection();
  }, [refreshConnection]);

  useEffect(() => {
    if (!connected || !connection?.id) return;
    if (loadedForRef.current === connection.id) return;
    void refreshAll();
  }, [connected, connection?.id, refreshAll]);

  const handleDraftAction = async (draftId: string, action: 'approve' | 'reject') => {
    if (!businessId) return;
    setDraftActionId(draftId);
    try {
      const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
        body: {
          action,
          businessId,
          connectionId: connection?.id,
          platform,
          draftId,
        },
      });
      if (error || data?.error) {
        toast.error(data?.error || (await invokeErrorMessage(error, data)) || 'Action failed');
        return;
      }
      toast.success(action === 'approve' ? `Published to ${ui.label}` : 'Draft removed');
      setOpenDraft(null);
      await Promise.all([loadUpcoming(), action === 'approve' ? loadPosts() : Promise.resolve()]);
    } catch (err) {
      console.error(err);
      toast.error('Action failed');
    } finally {
      setDraftActionId(null);
    }
  };

  const ensureDraftQueue = async () => {
    if (!businessId || !connection?.id) return;
    setFillingQueue(true);
    try {
      const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
        body: {
          action: 'fill_queue',
          businessId,
          connectionId: connection.id,
          platform,
        },
      });
      if (error || data?.error) {
        toast.error(data?.error || (await invokeErrorMessage(error, data)) || 'Failed to stage drafts');
        return;
      }
      const created = data?.created ?? 0;
      toast.success(
        created > 0
          ? `Staged ${created} AI post${created === 1 ? '' : 's'}`
          : `${data?.pending ?? 0} upcoming post${(data?.pending ?? 0) === 1 ? '' : 's'} ready`,
      );
      await loadUpcoming();
    } catch (err) {
      console.error(err);
      toast.error('Failed to stage drafts');
    } finally {
      setFillingQueue(false);
    }
  };

  const handleDelete = async (post: SocialPost) => {
    if (!businessId || !post.ayrsharePostId) return;
    if (!window.confirm(`Delete this ${ui.label} post? This cannot be undone.`)) return;
    setDeletingId(post.id);
    try {
      const { data, error } = await supabase.functions.invoke(ui.functionName, {
        body: {
          action: 'delete',
          businessId,
          connectionId: connection?.id,
          ayrsharePostId: post.ayrsharePostId,
          nexusHistoryId: post.nexusHistoryId,
        },
      });
      if (error || data?.error) {
        toast.error(
          data?.error || (await invokeErrorMessage(error, data)) || 'Failed to delete post',
        );
        return;
      }
      toast.success('Post deleted');
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete post');
    } finally {
      setDeletingId(null);
    }
  };

  if (!SOCIAL_ENABLED) return null;

  if (checking) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!connected || !connection) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm overflow-hidden">
            <PlatformIcon className="h-9 w-9" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{ui.connectTitle}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{ui.connectHint}</p>
          </div>
        </div>
        <SocialConnectButton
          platform={platform}
          size="large"
          onConnectionChange={() => {
            void refreshConnection();
          }}
        />
      </div>
    );
  }

  const pageName = connection.displayName || ui.accountLabel;

  const profileStats: { label: string; value: number }[] = [];
  if (platform === 'instagram') {
    if (typeof connection.mediaCount === 'number')
      profileStats.push({ label: 'posts', value: connection.mediaCount });
    if (typeof connection.followersCount === 'number')
      profileStats.push({ label: 'followers', value: connection.followersCount });
    if (typeof connection.followsCount === 'number')
      profileStats.push({ label: 'following', value: connection.followsCount });
  } else if (platform === 'linkedin') {
    if (typeof connection.followersCount === 'number')
      profileStats.push({ label: 'followers', value: connection.followersCount });
  } else {
    if (typeof connection.followersCount === 'number')
      profileStats.push({ label: 'followers', value: connection.followersCount });
    if (typeof connection.fanCount === 'number')
      profileStats.push({ label: 'likes', value: connection.fanCount });
  }

  const monthChips: { label: string; metric: MonthlyMetric | null; signed?: boolean }[] =
    monthlyStats
      ? [
          { label: 'Views', metric: monthlyStats.views },
          { label: 'Reach', metric: monthlyStats.reach },
          { label: 'New followers', metric: monthlyStats.newFollowers, signed: true },
          { label: 'Engagement', metric: monthlyStats.engagement },
        ]
      : [];

  return (
    <div className="space-y-5">
      {/* Month-to-date metrics bar (business-timezone month, computed server-side) */}
      {monthlyStats && (
        <section className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {monthlyStats.monthLabel} so far
          </p>
          <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
            {monthChips.map(({ label, metric, signed }) => (
              <div
                key={label}
                className="rounded-xl border border-gray-200 bg-white px-2.5 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)] min-w-0"
              >
                <div className="flex items-baseline gap-1 min-w-0">
                  <span className="text-[15px] font-bold text-gray-900 leading-tight truncate">
                    {metric
                      ? `${signed && metric.value > 0 ? '+' : ''}${
                          metric.value < 0 ? '−' : ''
                        }${fmtCount(Math.abs(metric.value))}`
                      : '—'}
                  </span>
                  {metric?.deltaPct != null && Math.round(Math.abs(metric.deltaPct)) > 0 ? (
                    <span
                      className={`text-[10px] font-semibold shrink-0 ${
                        metric.deltaPct > 0 ? 'text-green-600' : 'text-red-500'
                      }`}
                    >
                      {metric.deltaPct > 0 ? '▲' : '▼'}
                      {Math.round(Math.abs(metric.deltaPct))}%
                    </span>
                  ) : null}
                </div>
                <p className="text-[10px] text-gray-500 truncate mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Compact profile header — no mega card */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {connection.avatarUrl ? (
            <img
              src={connection.avatarUrl}
              alt=""
              className="h-10 w-10 rounded-full object-cover shrink-0 bg-[#e4e6eb]"
              referrerPolicy="no-referrer"
              onError={(e) => {
                const el = e.currentTarget;
                el.style.display = 'none';
                const fallback = el.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`h-10 w-10 rounded-full bg-white border border-gray-200 items-center justify-center shrink-0 overflow-hidden ${
              connection.avatarUrl ? 'hidden' : 'flex'
            }`}
            aria-hidden
          >
            <PlatformIcon className="h-10 w-10" square />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <h2
                className="text-[17px] font-bold text-gray-900 truncate leading-tight"
                style={{ letterSpacing: '-0.02em' }}
              >
                {pageName}
              </h2>
              <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" aria-hidden />
            </div>
            <p className="text-[12px] text-gray-500 truncate">
              {connection.username ? `@${connection.username}` : ui.accountLabel}
            </p>
            {profileStats.length > 0 && (
              <p className="text-[12px] text-gray-500 truncate mt-0.5">
                {profileStats.map((s, i) => (
                  <span key={s.label}>
                    {i > 0 ? <span className="mx-1 text-gray-300">·</span> : null}
                    <span className="font-semibold text-gray-900">{fmtCount(s.value)}</span>{' '}
                    {s.label}
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => void refreshAll(true)}
            disabled={loadingPosts || loadingUpcoming}
            aria-label="Refresh"
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {loadingPosts || loadingUpcoming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] text-[13px] font-semibold ${ui.primaryBtnClass}`}
            style={{ minHeight: 36 }}
          >
            <Plus className="h-4 w-4" />
            New post
          </button>
        </div>
      </div>

      {/* Upcoming — skinny strip, not full post cards. Tap a chip for details. */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-gray-900">
            <CalendarDays className="h-4 w-4 text-gray-500" />
            <h3 className="text-[13px] font-semibold" style={{ letterSpacing: '-0.01em' }}>
              Upcoming
              {upcoming.length > 0 ? (
                <span className="text-gray-500 font-medium"> · {upcoming.length} staged</span>
              ) : null}
            </h3>
          </div>
          {autoPostsEnabled && (
            <button
              type="button"
              onClick={() => void ensureDraftQueue()}
              disabled={fillingQueue}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 text-[11px] font-semibold hover:bg-blue-100 disabled:opacity-50"
            >
              {fillingQueue ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              {fillingQueue ? 'Staging…' : 'Stage to 5'}
            </button>
          )}
        </div>

        {loadingUpcoming && upcoming.length === 0 ? (
          <div className="flex gap-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                className="h-[60px] flex-1 rounded-xl border border-gray-200 bg-gray-100 animate-pulse"
              />
            ))}
          </div>
        ) : upcoming.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-4 py-3 text-[13px] text-gray-500 flex items-center justify-between gap-2">
            <span>{autoPostsEnabled ? 'No staged posts yet' : 'No upcoming posts'}</span>
            {autoPostsEnabled && (
              <button
                type="button"
                onClick={() => void ensureDraftQueue()}
                disabled={fillingQueue}
                className="shrink-0 text-[13px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                Stage 5 now
              </button>
            )}
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {upcoming.map((d) => {
              const url = mediaUrlOf(d);
              const isManual = (d.source || 'ai') === 'manual';
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setOpenDraft(d)}
                  className={`flex shrink-0 items-center gap-2.5 rounded-xl border bg-white px-2.5 py-2 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)] active:bg-gray-50 touch-manipulation ${
                    isManual ? 'border-gray-200' : 'border-amber-200'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {url ? (
                    <img
                      src={url}
                      alt=""
                      className="h-10 w-10 rounded-lg object-cover border border-gray-100 bg-gray-50"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
                      {isManual ? (
                        <Clock className="h-4 w-4" />
                      ) : (
                        <Sparkles className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-[13px] font-semibold text-gray-800 whitespace-nowrap">
                      {d.scheduled_for
                        ? formatStripWhen(d.scheduled_for, displayTz)
                        : 'Date pending'}
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </button>
              );
            })}
          </div>
        )}
      </section>

      <SocialCommentsInbox
        businessId={businessId}
        platform={platform}
        connectionId={connection?.id}
        refreshKey={inboxKey}
        onOpenPost={(postId) => {
          const hit = posts.find((p) => p.id === postId);
          if (hit) setOpenPost(hit);
          else toast('That post is older than the ones shown here. Open it on ' + ui.label + '.');
        }}
      />

      {/* Live grid owns the screen */}
      <section className="space-y-2">
        <div className="flex items-center gap-1.5 text-gray-900">
          <Grid3x3 className="h-4 w-4 text-gray-500" />
          <h3 className="text-[13px] font-semibold" style={{ letterSpacing: '-0.01em' }}>
            Posts
          </h3>
        </div>

        {loadingPosts && posts.length === 0 ? (
          <div className="grid grid-cols-3 gap-0.5 rounded-xl overflow-hidden">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="aspect-square bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">
            {ui.publishedEmpty}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-0.5 rounded-xl overflow-hidden">
              {posts.map((post) => (
                <div key={post.id} className="group relative aspect-square bg-gray-100">
                  {post.fullPicture ? (
                    <img
                      src={post.fullPicture}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center p-2 text-center bg-white">
                      <p className="text-[11px] text-gray-500 line-clamp-5 whitespace-pre-wrap">
                        {post.post || 'No caption'}
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpenPost(post)}
                    className="absolute inset-0"
                    aria-label="Open post and comments"
                  />
                  {post.likeCount != null || post.commentsCount != null ? (
                    <div className="absolute inset-x-0 bottom-0 flex items-center gap-2.5 px-2 pb-1.5 pt-6 bg-gradient-to-t from-black/60 via-black/25 to-transparent pointer-events-none">
                      {post.likeCount != null && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-white drop-shadow-sm">
                          <Heart className="h-3 w-3 fill-white" />
                          {fmtCount(post.likeCount)}
                        </span>
                      )}
                      {post.commentsCount != null && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-white drop-shadow-sm">
                          <MessageCircle className="h-3 w-3 fill-white" />
                          {fmtCount(post.commentsCount)}
                        </span>
                      )}
                    </div>
                  ) : null}
                  <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                    {post.canDelete && (
                      <button
                        type="button"
                        onClick={() => void handleDelete(post)}
                        disabled={deletingId === post.id}
                        className="p-1.5 rounded-lg bg-white/90 text-red-600 shadow-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        aria-label="Delete post"
                      >
                        {deletingId === post.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400">{ui.publishedHint}</p>
          </>
        )}
      </section>

      {/* Upcoming draft detail sheet */}
      {openDraft ? (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px] p-0 sm:p-4"
          onClick={() => setOpenDraft(null)}
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-[20px] sm:rounded-[20px] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {(() => {
              const d = openDraft;
              const url = mediaUrlOf(d);
              const isManual = (d.source || 'ai') === 'manual';
              return (
                <>
                  <div className="flex items-center justify-between px-5 pt-4 pb-2">
                    <div className="flex items-center gap-2">
                      {isManual ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                          Your post
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200">
                          <Sparkles className="h-3 w-3" />
                          AI draft
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-500">
                        <Clock className="h-3.5 w-3.5" />
                        {d.scheduled_for
                          ? `Publishes ${formatWhen(d.scheduled_for, displayTz)}`
                          : 'Date pending…'}
                        {` · ${postPlacementLabel(platform, d.metadata?.postFormat)}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenDraft(null)}
                      aria-label="Close"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="overflow-y-auto px-5 pb-4">
                    {url ? (
                      <img
                        src={url}
                        alt=""
                        className="w-full rounded-xl object-cover border border-gray-100 bg-gray-50 mb-3 max-h-72"
                      />
                    ) : d.has_image ? (
                      <div className="w-full h-32 rounded-xl border border-dashed border-blue-200 bg-blue-50 mb-3 flex items-center justify-center text-[12px] font-semibold text-blue-600">
                        Image attached
                      </div>
                    ) : null}
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{d.content}</p>
                  </div>
                  <div className="shrink-0 border-t border-gray-100 px-5 py-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => void handleDraftAction(d.id, 'approve')}
                      disabled={draftActionId === d.id}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[10px] bg-blue-600 text-white text-[13px] font-semibold active:bg-blue-700 disabled:opacity-50 touch-manipulation"
                      style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
                    >
                      {draftActionId === d.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      Publish now
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDraftAction(d.id, 'reject')}
                      disabled={draftActionId === d.id}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-[10px] border border-gray-300 text-gray-700 text-[13px] font-semibold active:bg-gray-50 disabled:opacity-50 touch-manipulation"
                      style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
                    >
                      <X className="h-4 w-4" />
                      Discard
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}

      <SocialPostSheet
        open={!!openPost}
        onClose={() => setOpenPost(null)}
        businessId={businessId}
        platform={platform}
        connectionId={connection?.id}
        post={openPost}
        onCommentsChanged={() => setInboxKey((k) => k + 1)}
      />

      <CreateSocialPostModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        platform={platform}
        connectionId={connection?.id}
        onSuccess={() => {
          void Promise.all([loadUpcoming(), loadPosts()]);
        }}
      />
    </div>
  );
}
